// src/components/ReportsView.js

import React, { useEffect, useState } from "react";
import { getReportSummary } from "../api";

// ============================================
// LOCAL DATE FORMAT
// ============================================

const toDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const emptySummary = {
  grossSales: 0,
  totalDiscount: 0,
  totalSales: 0,
  totalCost: 0,
  totalExpenses: 0,
  profit: 0,
  profitMargin: 0,

  cashCollected: 0,
  onlineCollected: 0,
  totalCollected: 0,
  outstandingAmount: 0,

  totalOrders: 0,

  dineInSales: 0,
  takeawaySales: 0,

  pos: {
    sales: 0,
    cash: 0,
    online: 0,
    orderCount: 0,
  },

  swiggy: {
    sales: 0,
    cash: 0,
    online: 0,
    orderCount: 0,
  },

  zomato: {
    sales: 0,
    cash: 0,
    online: 0,
    orderCount: 0,
  },
};

export default function ReportsView() {
  const today = new Date();

  const [start, setStart] = useState(
    toDateInput(
      new Date(
        today.getFullYear(),
        today.getMonth(),
        1
      )
    )
  );

  const [end, setEnd] = useState(
    toDateInput(
      new Date(
        today.getFullYear(),
        today.getMonth() + 1,
        0
      )
    )
  );

  const [summary, setSummary] = useState(emptySummary);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // ============================================
  // HELPERS
  // ============================================

  const number = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const money = (value) =>
    `₹${number(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const percent = (value) =>
    `${number(value).toFixed(2)}%`;

  const getChannel = (channel) => ({
    sales: number(channel?.sales),
    cash: number(channel?.cash),
    online: number(channel?.online),
    orderCount: number(channel?.orderCount),
  });

  // ============================================
  // LOAD REPORT
  // ============================================

  const loadSummary = async (
    selectedStart = start,
    selectedEnd = end
  ) => {

    if (!selectedStart || !selectedEnd) {
      alert("Please select both dates.");
      return;
    }

    if (selectedStart > selectedEnd) {
      alert("Start date cannot be after end date.");
      return;
    }

    setLoading(true);

    try {

      const data = await getReportSummary(
        selectedStart,
        selectedEnd
      );

      setSummary({
        ...emptySummary,
        ...data,

        pos: getChannel(data?.pos),
        swiggy: getChannel(data?.swiggy),
        zomato: getChannel(data?.zomato),
      });

      setLastUpdated(new Date());

    } catch (error) {

      console.error("Report loading failed:", error);

      const message =
        error?.response?.data?.message ||
        "Failed to load report data.";

      alert(message);

    } finally {

      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary(start, end);

    // The report loads once on initial page entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================
  // CHANNEL DATA
  // ============================================

  const channels = [
    {
      key: "pos",
      label: "Direct POS",
      description: "Restaurant POS orders",
      icon: "🏪",
      data: summary.pos,
      tone: "pos",
    },
    {
      key: "swiggy",
      label: "Swiggy",
      description: "Swiggy orders",
      icon: "🛵",
      data: summary.swiggy,
      tone: "swiggy",
    },
    {
      key: "zomato",
      label: "Zomato",
      description: "Zomato orders",
      icon: "🍽️",
      data: summary.zomato,
      tone: "zomato",
    },
  ];

  const maxChannelSales = Math.max(
    1,
    ...channels.map((channel) =>
      number(channel.data?.sales)
    )
  );

  const totalSales = number(summary.totalSales);
  const totalCollected = number(summary.totalCollected);

  const cashPercentage =
    totalCollected > 0
      ? (number(summary.cashCollected) / totalCollected) * 100
      : 0;

  const onlinePercentage =
    totalCollected > 0
      ? (number(summary.onlineCollected) / totalCollected) * 100
      : 0;

  const profitNegative = number(summary.profit) < 0;

  // ============================================
  // QUICK CARDS
  // ============================================

  const cards = [
    {
      key: "gross",
      label: "Gross Sales",
      value: summary.grossSales,
      tone: "sales",
    },
    {
      key: "discount",
      label: "Discounts",
      value: summary.totalDiscount,
      tone: "discount",
    },
    {
      key: "net",
      label: "Net Sales",
      value: summary.totalSales,
      tone: "net",
    },
    {
      key: "orders",
      label: "Total Orders",
      value: number(summary.totalOrders).toLocaleString("en-IN"),
      tone: "orders",
      isMoney: false,
    },
  ];

  return (
    <div className="so-reports">

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

        .so-reports {
          --ink: #1c1a17;
          --paper: #fbf9f4;
          --paper-dim: #f2ede1;
          --card: #fffdf8;
          --chili: #e4572e;
          --chili-dark: #c43f1a;
          --herb: #3f6b4f;
          --herb-dark: #2d4f39;
          --mustard: #b7791f;
          --blue: #35537d;
          --stone: #6b6459;
          --stone-light: #a49c8d;
          --line: #ddd4c2;

          --display: 'Bebas Neue', sans-serif;
          --body: 'Inter', sans-serif;
          --mono: 'JetBrains Mono', monospace;

          height: 100%;
          overflow: auto;
          padding: 28px;
          background-color: var(--paper-dim);
          background-image: radial-gradient(
            circle at 1px 1px,
            var(--line) 1px,
            transparent 1.6px
          );
          background-size: 24px 24px;
          font-family: var(--body);
          color: var(--ink);
        }

        .so-reports *,
        .so-reports *::before,
        .so-reports *::after {
          box-sizing: border-box;
        }

        .so-rep-head {
          display: flex;
          justify-content: space-between;
          align-items: end;
          gap: 16px;
          margin-bottom: 22px;
        }

        .so-rep-eyebrow {
          color: var(--chili);
          text-transform: uppercase;
          font-family: var(--mono);
          letter-spacing: .14em;
          font-size: .7rem;
          font-weight: 700;
        }

        .so-rep-title {
          margin: 5px 0 0;
          font-family: var(--display);
          font-size: 2.35rem;
          font-weight: 400;
          line-height: 1.05;
        }

        .so-rep-period-label {
          color: var(--stone);
          font-size: .75rem;
          font-family: var(--mono);
        }

        .so-rep-panel {
          padding: 18px;
          margin-bottom: 22px;
          background: var(--card);
          border: 1px solid var(--line);
          border-radius: 12px;
          box-shadow: 0 10px 26px -22px rgba(28,26,23,.35);
        }

        .so-rep-filter {
          display: grid;
          grid-template-columns: 1fr 1fr auto;
          gap: 14px;
          align-items: end;
        }

        .so-rep-field label {
          display: block;
          font-family: var(--mono);
          font-size: .72rem;
          font-weight: 700;
          color: var(--stone);
          margin-bottom: 7px;
        }

        .so-rep-field input {
          width: 100%;
          height: 46px;
          border: 1px solid var(--line);
          border-radius: 8px;
          padding: 8px 12px;
          background: var(--paper);
          color: var(--ink);
          font-size: 16px;
          outline: none;
        }

        .so-rep-field input:focus {
          border-color: var(--chili);
          box-shadow: 0 0 0 3px rgba(228,87,46,.12);
        }

        .so-rep-btn {
          height: 46px;
          border: 0;
          border-radius: 8px;
          padding: 0 22px;
          background: var(--chili);
          color: white;
          font-weight: 800;
          cursor: pointer;
          white-space: nowrap;
        }

        .so-rep-btn:hover:not(:disabled) {
          background: var(--chili-dark);
        }

        .so-rep-btn:disabled {
          opacity: .6;
          cursor: wait;
        }

        .so-rep-section-title {
          font-family: var(--display);
          font-size: 1.55rem;
          font-weight: 400;
          margin: 0 0 14px;
        }

        .so-rep-grid {
          display: grid;
          grid-template-columns: repeat(4,minmax(0,1fr));
          gap: 14px;
        }

        .so-rep-card {
          background: var(--card);
          border: 1px solid var(--line);
          border-radius: 12px;
          padding: 17px;
          position: relative;
          overflow: hidden;
          min-width: 0;
        }

        .so-rep-card::after {
          content: "";
          height: 4px;
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: var(--tone);
        }

        .so-rep-card.tone-sales { --tone: var(--chili); }
        .so-rep-card.tone-discount { --tone: var(--mustard); }
        .so-rep-card.tone-net { --tone: var(--herb); }
        .so-rep-card.tone-orders { --tone: var(--blue); }
        .so-rep-card.tone-cash { --tone: var(--herb); }
        .so-rep-card.tone-online { --tone: var(--blue); }
        .so-rep-card.tone-due { --tone: var(--chili); }
        .so-rep-card.tone-profit { --tone: var(--herb); }
        .so-rep-card.tone-loss { --tone: #a32618; }
        .so-rep-card.tone-cost { --tone: var(--mustard); }
        .so-rep-card.tone-expenses { --tone: var(--blue); }

        .so-rep-label {
          color: var(--stone);
          font-family: var(--mono);
          text-transform: uppercase;
          letter-spacing: .06em;
          font-size: .67rem;
          font-weight: 700;
        }

        .so-rep-value {
          font-size: 1.45rem;
          font-weight: 800;
          margin: 12px 0 3px;
          color: var(--tone);
          overflow-wrap: anywhere;
        }

        .so-rep-subtitle {
          color: var(--stone-light);
          font-size: .72rem;
        }

        .so-rep-two-col {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 18px;
        }

        .so-rep-payment-bar {
          display: flex;
          width: 100%;
          height: 14px;
          border-radius: 20px;
          overflow: hidden;
          background: var(--paper-dim);
          margin: 18px 0;
        }

        .so-rep-cash-bar {
          background: var(--herb);
          height: 100%;
        }

        .so-rep-online-bar {
          background: var(--blue);
          height: 100%;
        }

        .so-rep-legend {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 8px;
          margin: 11px 0;
          font-size: .86rem;
        }

        .so-rep-legend-left {
          display: flex;
          align-items: center;
          gap: 9px;
        }

        .so-rep-dot {
          width: 9px;
          height: 9px;
          border-radius: 50%;
          flex: none;
        }

        .so-rep-table-wrap {
          overflow-x: auto;
        }

        .so-rep-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 580px;
          font-size: .85rem;
        }

        .so-rep-table th {
          padding: 12px;
          font-family: var(--mono);
          font-size: .68rem;
          text-transform: uppercase;
          letter-spacing: .04em;
          background: var(--paper-dim);
          color: var(--stone);
          text-align: right;
          white-space: nowrap;
        }

        .so-rep-table th:first-child,
        .so-rep-table td:first-child {
          text-align: left;
        }

        .so-rep-table td {
          padding: 13px 12px;
          border-bottom: 1px solid var(--line);
          text-align: right;
          white-space: nowrap;
        }

        .so-rep-table tr:last-child td {
          border-bottom: none;
          font-weight: 800;
        }

        .so-rep-channel {
          display: flex;
          align-items: center;
          gap: 9px;
          font-weight: 700;
        }

        .so-rep-chart-row {
          margin-bottom: 17px;
        }

        .so-rep-chart-label {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          font-size: .84rem;
          font-weight: 600;
          margin-bottom: 7px;
        }

        .so-rep-chart-track {
          height: 12px;
          width: 100%;
          border-radius: 20px;
          background: var(--paper-dim);
          overflow: hidden;
        }

        .so-rep-chart-fill {
          height: 100%;
          border-radius: 20px;
          background: var(--chili);
        }

        .so-rep-note {
          color: var(--stone);
          font-size: .76rem;
          line-height: 1.6;
          margin-top: 13px;
        }

        .so-rep-profit {
          background: var(--card);
          border: 1px solid var(--line);
          border-radius: 12px;
          padding: 18px;
        }

        .so-rep-profit-line {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          border-bottom: 1px solid var(--line);
          padding: 12px 0;
          font-size: .86rem;
        }

        .so-rep-profit-line:last-child {
          border-bottom: none;
          font-weight: 800;
        }

        .so-rep-negative {
          color: #a32618;
        }

        .so-rep-positive {
          color: var(--herb-dark);
        }

        .so-rep-muted {
          color: var(--stone);
        }

        .so-rep-updated {
          font-family: var(--mono);
          font-size: .67rem;
          color: var(--stone-light);
          margin-top: 10px;
        }

        @media(max-width: 1000px) {
          .so-rep-grid {
            grid-template-columns: repeat(2,minmax(0,1fr));
          }
        }

        @media(max-width: 700px) {
          .so-reports {
            padding: 16px;
          }

          .so-rep-head {
            display: block;
          }

          .so-rep-title {
            font-size: 2rem;
          }

          .so-rep-filter {
            grid-template-columns: 1fr 1fr;
          }

          .so-rep-btn {
            grid-column: 1 / -1;
            width: 100%;
          }

          .so-rep-two-col {
            grid-template-columns: 1fr;
          }
        }

        @media(max-width: 420px) {
          .so-rep-grid {
            grid-template-columns: 1fr;
          }

          .so-rep-filter {
            grid-template-columns: 1fr;
          }

          .so-rep-btn {
            grid-column: auto;
          }
        }
      `}</style>

      {/* ============================================
          HEADER
      ============================================ */}

      <div className="so-rep-head">
        <div>
          <span className="so-rep-eyebrow">
            BUSINESS PERFORMANCE
          </span>

          <h2 className="so-rep-title">
            Reports / Sales & Payments
          </h2>
        </div>

        <div className="so-rep-period-label">
          {start} → {end}
        </div>
      </div>

      {/* ============================================
          DATE FILTER
      ============================================ */}

      <div className="so-rep-panel">
        <div className="so-rep-filter">

          <div className="so-rep-field">
            <label htmlFor="so-report-start">
              START DATE
            </label>

            <input
              id="so-report-start"
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </div>

          <div className="so-rep-field">
            <label htmlFor="so-report-end">
              END DATE
            </label>

            <input
              id="so-report-end"
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
          </div>

          <button
            type="button"
            className="so-rep-btn"
            onClick={() => loadSummary(start, end)}
            disabled={loading}
          >
            {loading ? "Loading..." : "Refresh Report"}
          </button>

        </div>

        {lastUpdated && (
          <p className="so-rep-updated">
            Last updated: {lastUpdated.toLocaleString("en-IN")}
          </p>
        )}

      </div>

      {/* ============================================
          SALES OVERVIEW
      ============================================ */}

      <h3 className="so-rep-section-title">
        Sales Overview
      </h3>

      <div className="so-rep-grid mb-6">

        {cards.map((card) => (
          <div
            key={card.key}
            className={`so-rep-card tone-${card.tone}`}
          >
            <p className="so-rep-label">
              {card.label}
            </p>

            <p className="so-rep-value">
              {card.isMoney === false
                ? card.value
                : money(card.value)}
            </p>
          </div>
        ))}

      </div>

      {/* ============================================
          PAYMENT COLLECTION
      ============================================ */}

      <h3 className="so-rep-section-title">
        Payments & Collections
      </h3>

      <div className="so-rep-grid mb-6">

        <div className="so-rep-card tone-cash">
          <p className="so-rep-label">
            Cash Collected
          </p>

          <p className="so-rep-value">
            {money(summary.cashCollected)}
          </p>
        </div>

        <div className="so-rep-card tone-online">
          <p className="so-rep-label">
            Online Collected
          </p>

          <p className="so-rep-value">
            {money(summary.onlineCollected)}
          </p>
        </div>

        <div className="so-rep-card tone-net">
          <p className="so-rep-label">
            Total Collected
          </p>

          <p className="so-rep-value">
            {money(summary.totalCollected)}
          </p>
        </div>

        <div className="so-rep-card tone-due">
          <p className="so-rep-label">
            Outstanding Balance
          </p>

          <p className="so-rep-value">
            {money(summary.outstandingAmount)}
          </p>
        </div>

      </div>

      {/* PAYMENT SPLIT BAR */}

      <div className="so-rep-panel mb-6">

        <h3 className="so-rep-section-title">
          Collection Breakdown
        </h3>

        <div className="so-rep-payment-bar">

          <div
            className="so-rep-cash-bar"
            style={{
              width: `${cashPercentage}%`,
            }}
          />

          <div
            className="so-rep-online-bar"
            style={{
              width: `${onlinePercentage}%`,
            }}
          />

        </div>

        <div className="so-rep-legend">
          <div className="so-rep-legend-left">
            <span
              className="so-rep-dot"
              style={{ background: "#3f6b4f" }}
            />
            Cash
          </div>

          <strong>
            {money(summary.cashCollected)}
          </strong>
        </div>

        <div className="so-rep-legend">
          <div className="so-rep-legend-left">
            <span
              className="so-rep-dot"
              style={{ background: "#35537d" }}
            />
            Online
          </div>

          <strong>
            {money(summary.onlineCollected)}
          </strong>
        </div>

        <p className="so-rep-note">
          Collection percentages are calculated from the
          cash and online amounts recorded on orders.
        </p>

      </div>

      {/* ============================================
          SALES BY CHANNEL
      ============================================ */}

      <h3 className="so-rep-section-title">
        Sales by Order Channel
      </h3>

      <div className="so-rep-panel mb-6">

        <div className="so-rep-table-wrap">

          <table className="so-rep-table">

            <thead>
              <tr>
                <th>Channel</th>
                <th>Sales</th>
                <th>Cash</th>
                <th>Online</th>
                <th>Orders</th>
              </tr>
            </thead>

            <tbody>

              {channels.map((channel) => (
                <tr key={channel.key}>

                  <td>
                    <div className="so-rep-channel">
                      <span>{channel.icon}</span>
                      {channel.label}
                    </div>
                  </td>

                  <td>
                    {money(channel.data.sales)}
                  </td>

                  <td>
                    {money(channel.data.cash)}
                  </td>

                  <td>
                    {money(channel.data.online)}
                  </td>

                  <td>
                    {channel.data.orderCount}
                  </td>

                </tr>
              ))}

              <tr>
                <td>Total</td>
                <td>{money(totalSales)}</td>
                <td>{money(summary.cashCollected)}</td>
                <td>{money(summary.onlineCollected)}</td>
                <td>{number(summary.totalOrders)}</td>
              </tr>

            </tbody>

          </table>

        </div>

        <p className="so-rep-note">
          Channel sales are calculated from final order totals
          after discounts. Cash and online show the amounts
          recorded against each channel.
        </p>

      </div>

      {/* ============================================
          CHANNEL COMPARISON
      ============================================ */}

      <div className="so-rep-panel mb-6">

        <h3 className="so-rep-section-title">
          Channel Sales Comparison
        </h3>

        {channels.map((channel) => {

          const width =
            (number(channel.data.sales) / maxChannelSales) * 100;

          return (
            <div
              key={channel.key}
              className="so-rep-chart-row"
            >

              <div className="so-rep-chart-label">
                <span>
                  {channel.icon} {channel.label}
                </span>

                <strong>
                  {money(channel.data.sales)}
                </strong>
              </div>

              <div className="so-rep-chart-track">
                <div
                  className="so-rep-chart-fill"
                  style={{ width: `${width}%` }}
                />
              </div>

            </div>
          );
        })}

      </div>

      {/* ============================================
          ORDER TYPE
      ============================================ */}

      <h3 className="so-rep-section-title">
        Order Type Breakdown
      </h3>

      <div className="so-rep-grid mb-6">

        <div className="so-rep-card tone-sales">
          <p className="so-rep-label">
            Dine In Sales
          </p>

          <p className="so-rep-value">
            {money(summary.dineInSales)}
          </p>
        </div>

        <div className="so-rep-card tone-online">
          <p className="so-rep-label">
            Takeaway Sales
          </p>

          <p className="so-rep-value">
            {money(summary.takeawaySales)}
          </p>
        </div>

      </div>

      {/* ============================================
          PROFIT AND COST
      ============================================ */}

      <h3 className="so-rep-section-title">
        Profit & Cost Analysis
      </h3>

      <div className="so-rep-profit mb-6">

        <div className="so-rep-profit-line">
          <span>Net Sales</span>
          <strong>{money(summary.totalSales)}</strong>
        </div>

        <div className="so-rep-profit-line">
          <span>Cost of Goods</span>
          <strong>{money(summary.totalCost)}</strong>
        </div>

        <div className="so-rep-profit-line">
          <span>Expenses (All Time)</span>
          <strong>{money(summary.totalExpenses)}</strong>
        </div>

        <div className="so-rep-profit-line">
          <span>Estimated Profit</span>
          <strong
            className={
              profitNegative
                ? "so-rep-negative"
                : "so-rep-positive"
            }
          >
            {money(summary.profit)}
          </strong>
        </div>

        <div className="so-rep-profit-line">
          <span>Profit Margin</span>
          <strong>
            {percent(summary.profitMargin)}
          </strong>
        </div>

        <p className="so-rep-note">
          Profit currently uses selected-period net sales and
          cost of goods, minus all-time expenses. It is not
          a date-matched profit figure until expense dates
          are included in the backend report.
        </p>

      </div>

      <p className="so-rep-period-label">
        Report period: {start} → {end}
      </p>

    </div>
  );
}










// // src/components/ReportsView.js
// import React, { useEffect, useState } from "react";
// import { getReportSummary } from "../api";

// // YYYY-MM-DD in LOCAL time (toISOString() shifts the date by the UTC offset,
// // which showed the wrong first day of the month in time zones ahead of UTC).
// const toDateInput = (date) => {
//   const year = date.getFullYear();
//   const month = String(date.getMonth() + 1).padStart(2, "0");
//   const day = String(date.getDate()).padStart(2, "0");

//   return `${year}-${month}-${day}`;
// };

// export default function ReportsView() {
//   const today = new Date();
//   const [start, setStart] = useState(
//     toDateInput(new Date(today.getFullYear(), today.getMonth(), 1))
//   );
//   const [end, setEnd] = useState(
//     toDateInput(new Date(today.getFullYear(), today.getMonth() + 1, 0))
//   );

//   const [summary, setSummary] = useState({
//     totalSales: 0,
//     totalCost: 0,
//     totalExpenses: 0,
//     profit: 0,
//   });

//   const [loading, setLoading] = useState(false);

//   const loadSummary = async (s, e) => {
//     setLoading(true);

//     try {
//       const data = await getReportSummary(s, e);
//       setSummary(data || {});
//     } catch (err) {
//       console.error(err);
//       alert("Failed to load report data");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     loadSummary(start, end);
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   const money = (value) => `₹${(Number(value) || 0).toFixed(2)}`;
//   const profitNegative = (summary.profit || 0) < 0;

//   const cards = [
//     { key: "sales", label: "Total Sales", value: summary.totalSales, tone: "sales" },
//     { key: "cost", label: "Cost of Goods", value: summary.totalCost, tone: "cost" },
//     {
//       key: "expenses",
//       label: "Expenses (All Time)",
//       value: summary.totalExpenses,
//       tone: "expenses",
//     },
//     {
//       key: "profit",
//       label: "Profit",
//       value: summary.profit,
//       tone: profitNegative ? "loss" : "profit",
//     },
//   ];

//   return (
//     <div className="so-reports">
//       {/* Scoped SwiftOrder theme: fonts, tokens and responsive rules live
//           here so this file can be dropped in on its own. */}
//       <style>{`
//         @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

//         .so-reports {
//           --ink: #1c1a17;
//           --paper: #fbf9f4;
//           --paper-dim: #f2ede1;
//           --chili: #e4572e;
//           --chili-dark: #c43f1a;
//           --accent: #b53a19;
//           --herb: #3f6b4f;
//           --mustard: #b7791f;
//           --blue: #35537d;
//           --stone: #6b6459;
//           --stone-light: #a49c8d;
//           --line: #ddd4c2;
//           --font-display: 'Bebas Neue', 'Arial Narrow', sans-serif;
//           --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
//           --font-mono: 'JetBrains Mono', 'Courier New', monospace;

//           height: 100%;
//           overflow: auto;
//           padding: 32px;
//           background: var(--paper-dim);
//           background-image: radial-gradient(circle at 1px 1px, var(--line) 1px, transparent 1.6px);
//           background-size: 24px 24px;
//           font-family: var(--font-body);
//           color: var(--ink);
//           -webkit-overflow-scrolling: touch;
//         }

//         .so-reports *,
//         .so-reports *::before,
//         .so-reports *::after {
//           box-sizing: border-box;
//         }

//         .so-rep-head {
//           margin-bottom: 22px;
//         }

//         .so-rep-eyebrow {
//           font-family: var(--font-mono);
//           font-size: 0.7rem;
//           letter-spacing: 0.14em;
//           text-transform: uppercase;
//           color: var(--chili);
//           font-weight: 600;
//           display: inline-flex;
//           align-items: center;
//           gap: 8px;
//         }

//         .so-rep-eyebrow::before {
//           content: "";
//           width: 6px;
//           height: 6px;
//           border-radius: 50%;
//           background: var(--chili);
//         }

//         .so-rep-title {
//           font-family: var(--font-display);
//           font-weight: 400;
//           font-size: 2.3rem;
//           letter-spacing: 0.01em;
//           line-height: 1.05;
//           margin: 6px 0 0;
//         }

//         .so-rep-filter {
//           background: #fffdf8;
//           border: 1px solid var(--line);
//           border-radius: 12px;
//           padding: 18px;
//           margin-bottom: 22px;
//           box-shadow: 0 10px 26px -22px rgba(28, 26, 23, 0.35);
//           display: grid;
//           grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
//           gap: 14px;
//           align-items: end;
//         }

//         .so-rep-field label {
//           display: block;
//           font-family: var(--font-mono);
//           font-size: 0.74rem;
//           font-weight: 600;
//           letter-spacing: 0.04em;
//           color: var(--stone);
//           margin-bottom: 6px;
//         }

//         .so-rep-field input {
//           width: 100%;
//           min-height: 46px;
//           border: 1px solid var(--line);
//           border-radius: 8px;
//           padding: 10px 12px;
//           font-family: var(--font-body);
//           font-size: 16px; /* 16px stops iOS from zooming into the field */
//           background: var(--paper);
//           color: var(--ink);
//           outline: none;
//           transition: border-color 0.15s ease, box-shadow 0.15s ease;
//         }

//         .so-rep-field input:focus {
//           border-color: var(--chili);
//           box-shadow: 0 0 0 3px rgba(228, 87, 46, 0.15);
//         }

//         .so-rep-btn {
//           min-height: 46px;
//           padding: 0 22px;
//           border: 0;
//           border-radius: 8px;
//           background: var(--chili);
//           color: var(--paper);
//           font-family: var(--font-body);
//           font-weight: 700;
//           font-size: 0.92rem;
//           cursor: pointer;
//           box-shadow: 0 10px 22px -10px rgba(228, 87, 46, 0.55);
//           transition: background 0.15s ease, opacity 0.15s ease;
//         }

//         .so-rep-btn:hover:not(:disabled) {
//           background: var(--chili-dark);
//         }

//         .so-rep-btn:disabled {
//           opacity: 0.65;
//           cursor: wait;
//         }

//         .so-rep-grid {
//           display: grid;
//           grid-template-columns: repeat(4, minmax(0, 1fr));
//           gap: 16px;
//         }

//         .so-rep-card {
//           position: relative;
//           background: #fffdf8;
//           border: 1px solid var(--line);
//           border-radius: 12px;
//           padding: 18px 18px 22px;
//           overflow: hidden;
//           box-shadow: 0 10px 26px -22px rgba(28, 26, 23, 0.3);
//         }

//         .so-rep-card::after {
//           content: "";
//           position: absolute;
//           left: 0;
//           right: 0;
//           bottom: 0;
//           height: 4px;
//           background: var(--tone);
//         }

//         .so-rep-card.tone-sales { --tone: var(--chili); }
//         .so-rep-card.tone-cost { --tone: var(--mustard); }
//         .so-rep-card.tone-expenses { --tone: var(--blue); }
//         .so-rep-card.tone-profit { --tone: var(--herb); }
//         .so-rep-card.tone-loss { --tone: #8f2412; }

//         .so-rep-label {
//           display: flex;
//           align-items: center;
//           gap: 8px;
//           font-family: var(--font-mono);
//           font-size: 0.7rem;
//           letter-spacing: 0.08em;
//           text-transform: uppercase;
//           color: var(--stone);
//           font-weight: 600;
//         }

//         .so-rep-label::before {
//           content: "";
//           flex: none;
//           width: 8px;
//           height: 8px;
//           border-radius: 50%;
//           background: var(--tone);
//         }

//         .so-rep-value {
//           margin: 12px 0 0;
//           font-size: 1.6rem;
//           font-weight: 800;
//           letter-spacing: -0.01em;
//           color: var(--tone);
//           overflow-wrap: anywhere;
//         }

//         .so-rep-period {
//           margin: 18px 0 0;
//           font-family: var(--font-mono);
//           font-size: 0.74rem;
//           color: var(--stone);
//         }

//         /* Tablet: two cards per row, button full width */
//         @media (max-width: 1023px) {
//           .so-rep-grid {
//             grid-template-columns: repeat(2, minmax(0, 1fr));
//           }
//         }

//         /* Phone */
//         @media (max-width: 640px) {
//           .so-reports {
//             padding: 16px;
//           }

//           .so-rep-title {
//             font-size: 2rem;
//           }

//           .so-rep-filter {
//             grid-template-columns: 1fr 1fr;
//             padding: 14px;
//             gap: 12px;
//           }

//           .so-rep-btn {
//             grid-column: 1 / -1;
//             width: 100%;
//           }

//           .so-rep-grid {
//             gap: 12px;
//           }

//           .so-rep-card {
//             padding: 14px 14px 18px;
//           }

//           .so-rep-value {
//             font-size: 1.3rem;
//           }
//         }

//         /* Small phone: stack everything */
//         @media (max-width: 420px) {
//           .so-rep-filter {
//             grid-template-columns: 1fr;
//           }

//           .so-rep-grid {
//             grid-template-columns: 1fr;
//           }

//           .so-rep-value {
//             font-size: 1.55rem;
//           }
//         }
//       `}</style>

//       <div className="so-rep-head">
//         <span className="so-rep-eyebrow">Business summary</span>
//         <h2 className="so-rep-title">Reports / Profit</h2>
//       </div>

//       <div className="so-rep-filter">
//         <div className="so-rep-field">
//           <label htmlFor="so-rep-start">Start Date</label>
//           <input
//             id="so-rep-start"
//             type="date"
//             value={start}
//             onChange={(e) => setStart(e.target.value)}
//           />
//         </div>

//         <div className="so-rep-field">
//           <label htmlFor="so-rep-end">End Date</label>
//           <input
//             id="so-rep-end"
//             type="date"
//             value={end}
//             onChange={(e) => setEnd(e.target.value)}
//           />
//         </div>

//         <button
//           type="button"
//           className="so-rep-btn"
//           onClick={() => loadSummary(start, end)}
//           disabled={loading}
//         >
//           {loading ? "Loading..." : "Refresh"}
//         </button>
//       </div>

//       <div className="so-rep-grid">
//         {cards.map((card) => (
//           <div key={card.key} className={`so-rep-card tone-${card.tone}`}>
//             <p className="so-rep-label">{card.label}</p>
//             <p className="so-rep-value">{money(card.value)}</p>
//           </div>
//         ))}
//       </div>

//       <p className="so-rep-period">
//         Selected period: {start} → {end}
//       </p>
//     </div>
//   );
// }








// // src/components/ReportsView.js
// import React, { useEffect, useState } from "react";
// import { getReportSummary } from "../api";

// export default function ReportsView() {
//   const today = new Date();
//   const [start, setStart] = useState(
//     new Date(today.getFullYear(), today.getMonth(), 1)
//       .toISOString()
//       .slice(0, 10)
//   );
//   const [end, setEnd] = useState(
//     new Date(today.getFullYear(), today.getMonth() + 1, 0)
//       .toISOString()
//       .slice(0, 10)
//   );

//   const [summary, setSummary] = useState({
//     totalSales: 0,
//     totalCost: 0,
//     totalExpenses: 0,
//     profit: 0,
//   });

//   const loadSummary = async (s, e) => {
//     try {
//       const data = await getReportSummary(s, e);
//       setSummary(data || {});
//     } catch (err) {
//       console.error(err);
//       alert("Failed to load report data");
//     }
//   };

//   useEffect(() => {
//     loadSummary(start, end);
//   }, []);

//   return (
//     <div className="p-8 h-full overflow-auto">
//       <div className="flex justify-between items-center mb-6">
//         <h2 className="text-3xl font-bold text-slate-800">
//           Reports / Profit
//         </h2>
//       </div>

//       <div className="bg-white rounded-lg shadow p-4 mb-6 flex gap-4 items-end">
//         <div>
//           <label className="block text-sm mb-1">Start Date</label>
//           <input
//             type="date"
//             value={start}
//             onChange={(e) => setStart(e.target.value)}
//             className="border rounded px-2 py-1"
//           />
//         </div>
//         <div>
//           <label className="block text-sm mb-1">End Date</label>
//           <input
//             type="date"
//             value={end}
//             onChange={(e) => setEnd(e.target.value)}
//             className="border rounded px-2 py-1"
//           />
//         </div>
//         <button
//           onClick={() => loadSummary(start, end)}
//           className="bg-blue-600 text-white px-4 py-2 rounded"
//         >
//           Refresh
//         </button>
//       </div>

//       <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
//         <div className="bg-white rounded-lg shadow p-4">
//           <p className="text-xs text-slate-500">Total Sales</p>
//           <p className="text-2xl font-bold">
//             ₹{(summary.totalSales || 0).toFixed(2)}
//           </p>
//         </div>
//         <div className="bg-white rounded-lg shadow p-4">
//           <p className="text-xs text-slate-500">Cost of Goods</p>
//           <p className="text-2xl font-bold">
//             ₹{(summary.totalCost || 0).toFixed(2)}
//           </p>
//         </div>
//         <div className="bg-white rounded-lg shadow p-4">
//           <p className="text-xs text-slate-500">Expenses (All Time)</p>
//           <p className="text-2xl font-bold">
//             ₹{(summary.totalExpenses || 0).toFixed(2)}
//           </p>
//         </div>
//         <div className="bg-white rounded-lg shadow p-4">
//           <p className="text-xs text-slate-500">Profit</p>
//           <p
//             className={`text-2xl font-bold ${
//               (summary.profit || 0) >= 0
//                 ? "text-emerald-600"
//                 : "text-red-600"
//             }`}
//           >
//             ₹{(summary.profit || 0).toFixed(2)}
//           </p>
//         </div>
//       </div>
//     </div>
//   );
// }
